import map from 'lodash/map';
import keyBy from 'lodash/keyBy';
import noop from 'lodash/noop';
import moment from 'moment';
import { 
  success as showSuccessNotification,
  error as showErrorNotification
} from 'zoomforth_app_basics/action_creators/notifications.js';

import foldersStore from 'zoomforth_zf/appglobals/page-folders.js';
import routesStore from 'zoomforth_zf/appglobals/routes.js';
import { defaultDatetimeFormat } from 'zoomforth_zfutils/datetime_formatting.js';

import {
  getOrderedAncestorFolderObjs,
  makeFolderObjsFromFolderTree
} from 'zoomforth_shared_management/lib/page_folders.js';

import {
  getFolderTree
} from '../reducers/index.js';

import { getForPage as getPageHistoryTrackerForPage } from 'zoomforth_zf/appglobals/page-history-tracker.js';
import { getPageChangeApproverURL } from 'zoomforth_page_change_approver/utils.js';


export const deleteRoute = (route)=>
  (dispatch)=> {
    route.destroy()
      .done(()=> {
        dispatch(showSuccessNotification({
          message: `Deleted '${route.getNameOrUrl()}' successfully.`
        }));
      });
    dispatch({ type: 'DELETE_ROUTE', routeId:route.id });
  };

export const addRouteToRouteCollection = (route)=>
  (dispatch)=> {
    getPageHistoryTrackerForPage(route.get('resource_id')).trackCreateRoute();
    routesStore.get().add(route);
    dispatch({ type: 'ADD_ROUTE', routeId: route.id }); // Not handled
  };
  
export const deletePage = (page)=>
  (dispatch)=> {
    page.destroy()
      .done(()=> {
        dispatch(showSuccessNotification({
          message: `Deleted '${page.get('unpublishedName')}' successfully.`
        }));
      });
    dispatch({ type: 'DELETE_PAGE', pageId:page.id });
  };


export const downloadScreenshot = (page)=>
  ()=> {
    window.open(page.get('screenshot_fullpage') || page.get('screenshot_standard'));
  };

export const downloadEditHistory = (page)=>
  ()=> {
    window.open(`/download/page_history.csv?id=${page.id}&output_utc_offset=${moment().format('Z')}`, '_blank');
  };

export const downloadFailedAccessLogs = (page)=>
  ()=> {
    window.open(`/download/failed_access.csv?id=${page.get('id')}`, '_blank');
  };

export const setFaviconUrlByTypeForPage = (page, faviconUrlByType, { onSuccess=noop, onError=noop })=>
  (dispatch)=> {
    page.save({
      'favicon_url_by_type': faviconUrlByType
    }, {
      patch:true,
      success: ()=> {
        onSuccess();

        dispatch(showSuccessNotification({
          message: 'The site favicon was updated'
        }));
      },
      error: onError
    });
  };


export const deactivatePage = (page)=>
  (dispatch)=> {
    page.save({
      'is_deactivated': true

    }, { patch:true })

      .done(()=> {
        dispatch(showSuccessNotification({
          message: `The page '${page.get('unpublishedName')}' was deactivated—it is no longer accessible via any links.`
        }));

        getPageHistoryTrackerForPage(page.id).trackDeactivatePage();
      });    
  };

export const schedulePageDeactivation = (site, date, { success=noop, error=noop, done=noop }={}) => (dispatch)=> {
  site.save(
    { 'deactivation_scheduled_at': date.valueOf() }, 
    { 
      patch:true,
      success: () => {
        success();

        dispatch(showSuccessNotification({
          message: `The site '${site.get('unpublishedName')}' has been scheduled for deactivation on ${defaultDatetimeFormat(date)}. Once deactivated it will no longer be accessible via any links.`
        }));
    
        getPageHistoryTrackerForPage(site.id).trackScheduledDeactivationForSite(date);
      },
      error: () => {
        error();

        dispatch(showErrorNotification({
          message: `There was an error scheduling the site '${site.get('unpublishedName')}' for deactivation. Please try again later.`
        }));
      }
    }
  ).done(() => done()); 
};

export const removeScheduledPageDeactivation = (site, { success=noop, error=noop, done=noop }={}) => (dispatch) => {
  site.save(
    { 'deactivation_scheduled_at': null }, 
    { 
      patch:true,
      success: ()=> {
        success();

        dispatch(showSuccessNotification({
          message: `The site '${site.get('unpublishedName')}' is no longer scheduled for deactivation.`
        }));

        getPageHistoryTrackerForPage(site.id).trackRemovedScheduledDeactivationForSite();
      },
      error: () => {
        error();

        dispatch(showErrorNotification({
          message: `There was an error removing the scheduled deactivation for the site '${site.get('unpublishedName')}'. Please try again later.`
        }));
      }
    }
  ).done(() => done());
};

export const reactivatePage = (page)=>
  (dispatch)=> {
    page.save({
      'is_deactivated': false

    }, { patch:true })

      .done(()=> {
        dispatch(showSuccessNotification({
          message: `The page '${page.get('unpublishedName')}' was reactivated.'`
        }));

        getPageHistoryTrackerForPage(page.id).trackReactivatePage();
      });    
  };

export const archivePage = (page, { windowLocationOnSuccess }={})=>
  (dispatch)=> {
    page.save({
      'is_archived': true

    }, { patch:true })

      .done(()=> {
        dispatch(showSuccessNotification({
          message: `The page '${page.get('unpublishedName')}' was archived.`
        }));

        getPageHistoryTrackerForPage(page.id).trackArchivePage();

        if (windowLocationOnSuccess) {
          window.location = windowLocationOnSuccess;
        }
      });    
  };


export const unarchivePage = (page)=>
  (dispatch)=> {
    page.save({
      'is_archived': false

    }, { patch:true })

      .done(()=> {
        dispatch(showSuccessNotification({
          message: `The page '${page.get('unpublishedName')}' was unarchived.'`
        }));

        getPageHistoryTrackerForPage(page.id).trackUnarchivePage();
      });    
  };


export const review = (page)=>
  ()=> {
    window.open(getPageChangeApproverURL(page.get('urlId')), '_blank');
  };


export const setFolderForPage = ({ page, folder })=>

  (dispatch)=> {
    const folders = map(foldersStore.get().models, f => f.toJSON());

    const ancestorFolderObjs = getOrderedAncestorFolderObjs(
      folders,
      'id',
      folder.id
    );

    const folderPath = map([...ancestorFolderObjs, folder.toJSON()], 'name').join(' > ');

    page.save({
      folder_id: folder.id

    }, { patch:true })

      .done(()=> {
        dispatch(showSuccessNotification({
          message: `Successfully moved site '${page.get('unpublishedName')}' to folder '${folderPath}'`
        }));
      });
  };
